import { SurveyType } from '../../../../../shared/constants';
import {
  isSurveyAttentionResponse,
  SURVEY_SCALE_BY_TYPE,
  surveyScoreToLabel,
} from '../../../../../shared/surveys';
import type {
  SurveyInsightsDistributionEntry,
  SurveyInsightsGranularity,
} from '../../../../../store/hubOwner/types';
import type {
  FacilitySurveyInsightsBlock,
  FacilitySurveyLowestScore,
  FacilitySurveyParticipation,
} from '../../../../../store/facility/facilityTypes';
import {
  bucketUnitLabel,
  computeTrend,
  MIN_BUCKETS_FOR_TREND,
  SurveyTrendDirection,
} from '../../../../Patients/PatientDetails/components/InsightsTab/surveyInsights';
import {
  StatCard,
  StatCardsStatus,
  TREND_LABELS,
  UNAVAILABLE_NOTE,
} from '../../../../Patients/PatientDetails/components/InsightsTab/surveyStatCards';
import {
  faArrowTrendDown,
  faArrowTrendUp,
  faMinus,
} from '@fortawesome/pro-light-svg-icons';
import { customError, customSuccess, dimGrey } from '../../../../../mainTheme';

// Location-level figures for one survey type over the selected window, from
// GET /facility/:id/survey_insights. Every count is a patient-day (answers by
// one patient on one day are counted as per survey cadence) and averages
// cover responders only.
export type FacilitySurveyStats = {
  // Patient-days answered inside the window.
  count: number;
  // Mean score over the window; null with no responses.
  average: number | null;
  lowest: SurveyInsightsDistributionEntry | null;
  highest: SurveyInsightsDistributionEntry | null;
  participation: FacilitySurveyParticipation;
  trend: SurveyTrendDirection;
  trendDelta: number | null;
};

export const EMPTY_PARTICIPATION: FacilitySurveyParticipation = {
  patients_eligible: 0,
  patients_responded: 0,
  patients_not_responded: 0,
  participation_rate: 0,
};

const toNumber = (value: unknown): number => Number(value) || 0;

export const computeFacilitySurveyStats = (
  block: FacilitySurveyInsightsBlock | null | undefined,
  // The current roster: the denominator for a response that predates
  // `participation.patients_eligible` (served from the API's cache).
  patientCount = 0,
): FacilitySurveyStats => {
  const answered = (block?.distribution ?? []).filter(
    (d) => toNumber(d.count) > 0,
  );
  const p = block?.participation;
  return {
    count: toNumber(block?.response_count),
    average: block?.average_score == null ? null : Number(block.average_score),
    lowest: answered.length
      ? answered.reduce((a, b) => (b.score < a.score ? b : a))
      : null,
    highest: answered.length
      ? answered.reduce((a, b) => (b.score > a.score ? b : a))
      : null,
    participation: p
      ? {
          patients_eligible: toNumber(p.patients_eligible ?? patientCount),
          patients_responded: toNumber(p.patients_responded),
          patients_not_responded: toNumber(p.patients_not_responded),
          participation_rate: toNumber(p.participation_rate),
        }
      : EMPTY_PARTICIPATION,
    ...computeTrend(block?.trend),
  };
};

// Rows for the "Lowest scores" table: the responders whose lowest answer in
// the window is in the attention range the Overview dashboard flags
// (SURVEY_ATTENTION_RESPONSES), in the API's order — worst first, then by
// name. Responders outside that range are left out, so a location where
// everyone answered Neutral or better lists nobody.
export const selectLowestScoreRows = (
  block: FacilitySurveyInsightsBlock | null | undefined,
  surveyType: SurveyType,
): FacilitySurveyLowestScore[] =>
  (block?.lowest_scores ?? []).filter((row) =>
    isSurveyAttentionResponse(surveyType, row.score),
  );

// 'Harry Potter', falling back to the id when the record has no name.
export const lowestScoreDisplayName = (
  row: Pick<
    FacilitySurveyLowestScore,
    'customer_id' | 'first_name' | 'last_name'
  >,
): string =>
  `${row.first_name ?? ''} ${row.last_name ?? ''}`.trim() ||
  `Patient #${row.customer_id}`;

// '54.5%' for the share of the roster that answered.
export const formatRate = (rate: number): string => `${rate}%`;

const TREND_ICONS: Record<SurveyTrendDirection, StatCard['icon']> = {
  improving: { icon: faArrowTrendUp, color: customSuccess },
  declining: { icon: faArrowTrendDown, color: customError },
  steady: { icon: faMinus, color: dimGrey },
  insufficient: undefined,
};

const describeEntry = (
  entry: SurveyInsightsDistributionEntry | null,
  surveyType: SurveyType,
): string | null =>
  entry
    ? `${entry.label ?? surveyScoreToLabel(surveyType, entry.score) ?? 'N/A'} · ${
        entry.count
      }×`
    : null;

const formatDelta = (delta: number | null): string => {
  if (delta === null) return '';
  const sign = delta > 0 ? '+' : '';
  return `${sign}${delta} vs earlier in window`;
};

// Tile contents for the location Insights tab. Mirrors the patient tiles but
// counts patient-days across the roster and swaps the "latest score" tile
// (one patient's most recent answer) for participation.
export const buildFacilityStatCards = (
  stats: FacilitySurveyStats,
  surveyType: SurveyType,
  windowLabel: string,
  granularity: SurveyInsightsGranularity | null | undefined,
  status: StatCardsStatus = 'ready',
): StatCard[] => {
  const { max } = SURVEY_SCALE_BY_TYPE[surveyType];
  const outOf = (score: number | null | undefined) =>
    score == null ? 'N/A' : `${score} / ${max}`;
  const windowNote = windowLabel.toLowerCase();
  const { participation } = stats;
  const cards: StatCard[] = [
    {
      key: 'count',
      label: 'Responses',
      value: String(stats.count),
      sub: windowNote,
      description: `Patient-days in the ${windowNote} on which a patient of this location answered this survey. Several answers by one patient on one day are counted as per survey cadence.`,
    },
    {
      key: 'participation',
      label: 'Participation',
      value: `${participation.patients_responded} of ${participation.patients_eligible}`,
      sub: `${formatRate(participation.participation_rate)} of patients answered`,
      description: `Patients who answered this survey at least once in the ${windowNote}, out of the patients assigned to this location at any point in it.`,
      icon: undefined,
    },
    {
      key: 'average',
      label: 'Average score',
      value: outOf(stats.average),
      sub: windowNote,
      description: `Mean of the patient-day scores on the 1-${max} scale over the ${windowNote}; patients who did not answer are not counted.`,
    },
    {
      key: 'lowest',
      label: 'Lowest score',
      value: outOf(stats.lowest?.score),
      sub: describeEntry(stats.lowest, surveyType),
      description: `Lowest score given in the ${windowNote}, with the number of patient-days it was given.`,
    },
    {
      key: 'highest',
      label: 'Highest score',
      value: outOf(stats.highest?.score),
      sub: describeEntry(stats.highest, surveyType),
      description: `Highest score given in the ${windowNote}, with the number of patient-days it was given.`,
    },
    {
      key: 'trend',
      label: 'Trend',
      value: TREND_LABELS[stats.trend],
      sub:
        stats.trend === 'insufficient'
          ? `Needs ${MIN_BUCKETS_FOR_TREND}+ answered ${bucketUnitLabel(granularity)}`
          : formatDelta(stats.trendDelta),
      description:
        'Compares the average of the most recent half of the answered periods in the window with the average of the earlier half.',
      icon: TREND_ICONS[stats.trend] && {
        ...TREND_ICONS[stats.trend]!,
        testId: `trend-icon-${stats.trend}`,
      },
    },
  ];
  if (status === 'ready') return cards;
  return cards.map((card) =>
    status === 'loading'
      ? { ...card, value: '', sub: null, icon: undefined, pending: true }
      : { ...card, value: 'N/A', sub: UNAVAILABLE_NOTE, icon: undefined },
  );
};
